/**
 * PortalDataGridPro
 *
 * A shared wrapper around MUI DataGridPro that also supports a mobile card layout.
 *
 * Props:
 *   rows: Array<Object> - data rows for the grid/card view.
 *   columns: Array<Object> - column definitions passed to DataGridPro.
 *   getRowId: Function (optional) - function to generate a unique row id.
 *   isSmall: Boolean (optional) - small screen flag from WithSmallScreen.
 *   columnVisibilityModel: Object (optional) - default/fallback visibility settings for both desktop and mobile views.
 *   desktopColumnVisibilityModel: Object (optional) - desktop/grid-only visibility settings for columns.
 *   desktopColumns: Array<String> (optional) - explicit desktop-only column field list.
 *   mobileColumnVisibilityModel: Object (optional) - mobile/card-only visibility settings for columns.
 *   mobileColumns: Array<String> (optional) - explicit mobile-only column field list.
 *   useMobileCardLayout: Boolean (optional) - enable card layout on mobile/small screens (opt-in; default false).
 *   baseId: String (optional) - base ID prefix for generated element IDs.
 *   cardHeaderVariant: String (optional) - header variant for mobile card labels (defaults to 'h3').
 *   onRowClick: Function (optional) - row click handler for DataGridPro.
 *   handleClick: Function (optional) - alternate row click handler for DataGridPro.
 *   loading: Boolean (optional) - grid loading state.
 *   slots: Object (optional) - DataGridPro slots override.
 *   slotProps: Object (optional) - DataGridPro slot props.
 *   disableColumnFilter: Boolean (optional)
 *   disableColumnSelector: Boolean (optional)
 *   disableDensitySelector: Boolean (optional)
 *   pagination: Boolean (optional)
 *   initialState: Object (optional)
 *   className: String (optional)
 *   id: String (optional)
 */
import React from 'react';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useTheme } from '@emotion/react'; //to detect small screens
import { useMediaQuery, Box, Card, CardContent, Typography, Stack, Divider } from '@mui/material';

const getRawValue = (col, row) => {
  let result;

  if (!row) {
    result = undefined;
  } else if (typeof col.valueGetter === 'function') {
    //checking for functions used for formatting
    const value = row[col.field];
    if (col.valueGetter.length > 1) {
      result = col.valueGetter(value, row);
    } else {
      result = col.valueGetter({ row, field: col.field, colDef: col, value });
    }
  } else {
    result = row[col.field];
  }

  return result;
};

const formatValue = (col, row) => {
  const rawValue = getRawValue(col, row);
  let result =
    typeof col.valueFormatter === 'function'
      ? col.valueFormatter({ value: rawValue, row, field: col.field, colDef: col })
      : rawValue;

  if (result === null || result === undefined) {
    result = '';
  } else if (Array.isArray(result)) {
    result = result.join(', ');
  } else if (typeof result === 'boolean') {
    //TODO:update this later
    result = result ? 'True' : 'False';
  }

  return result;
};

//can mark some cols invisible for viewing on small screens
const getVisibleColumns = (columns, columnVisibilityModel, visibleFields) => {
  const baseColumns = Array.isArray(visibleFields)
    ? (columns || []).filter((col) => visibleFields.includes(col.field))
    : columns || [];

  const visible = [];
  baseColumns.forEach((col) => {
    let shouldInclude = true;

    if (col.hide) {
      shouldInclude = false;
    } else if (
      columnVisibilityModel &&
      Object.prototype.hasOwnProperty.call(columnVisibilityModel, col.field)
    ) {
      shouldInclude = columnVisibilityModel[col.field] !== false;
    }

    if (shouldInclude) {
      visible.push(col);
    }
  });

  return visible;
};

const PortalDataGridPro = (props) => {
  const theme = useTheme();
  const breakpointIsSmall = useMediaQuery(theme.breakpoints.down('md'));
  const {
    useMobileCardLayout,
    desktopColumnVisibilityModel,
    desktopColumns,
    mobileColumnVisibilityModel,
    mobileColumns,
    baseId,
    cardHeader = 'h3',
    isSmall,
    className,
    id,
    rows,
    columns,
    columnVisibilityModel: defaultColumnVisibilityModel,
    onRowClick,
    handleClick,
    getRowId,
    ...dataGridProps
  } = props;
  const componentBaseId = baseId ?? id ?? 'portalDataGridPro';
  const useCardLayout = useMobileCardLayout === true && (isSmall || breakpointIsSmall);
  const mobileVisibilityModel = mobileColumnVisibilityModel ?? defaultColumnVisibilityModel;
  const mobileVisibleColumns = getVisibleColumns(columns, mobileVisibilityModel, mobileColumns);
  const desktopColumnsToRender = desktopColumns
    ? (columns || []).filter((col) => desktopColumns.includes(col.field))
    : columns;
  const desktopVisibilityModel = desktopColumnVisibilityModel ?? defaultColumnVisibilityModel;

  if (useCardLayout) {
    return (
      <Box className={className} id={id ? `${id}_container` : `${componentBaseId}_container`}>
        <Stack spacing={2}>
          {(rows || []).map((row) => {
            const rowKey = getRowId ? getRowId(row) : row.id;
            const rowId = String(rowKey).replace(/[^a-zA-Z0-9_-]/g, '_');
            const cardContent = (
              <CardContent sx={{ p: 2 }}>
                <Stack spacing={1}>
                  {mobileVisibleColumns.map((col) => {
                    const fieldId = col.field
                      ? String(col.field).replace(/[^a-zA-Z0-9_-]/g, '_')
                      : 'field';
                    const cellValue = col.renderCell
                      ? col.renderCell({
                          row,
                          field: col.field,
                          colDef: col,
                          value: row[col.field],
                        })
                      : formatValue(col, row);

                    return (
                      <Box key={col.field} id={`${componentBaseId}_row_${rowId}_${fieldId}_box`}>
                        <Typography
                          id={`${componentBaseId}_row_${rowId}_${fieldId}_label`}
                          component={cardHeader}
                          sx={{ fontWeight: 'bold' }}
                        >
                          {col.headerName || col.field}
                        </Typography>
                        {/* //check if react element, otherwise its a string */}
                        {React.isValidElement(cellValue) ? (
                          cellValue
                        ) : (
                          <Typography
                            id={`${componentBaseId}_row_${rowId}_${fieldId}_value`}
                            component="span"
                            sx={{ whiteSpace: 'pre-wrap' }}
                          >
                            {cellValue}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              </CardContent>
            );

            return (
              <Card
                key={rowKey}
                id={`${componentBaseId}_row_${rowId}_card`}
                variant="outlined"
                sx={{ width: '100%' }}
              >
                {cardContent}
              </Card>
            );
          })}
        </Stack>
      </Box>
    );
  }

  return (
    <React.Fragment>
      <DataGridPro
        {...dataGridProps}
        className={className}
        id={id || `${componentBaseId}_grid`}
        density="compact"
        experimentalFeatures={{ ariaV7: true }} //try some a11y stuff?
        rows={rows}
        columns={desktopColumnsToRender}
        columnVisibilityModel={desktopVisibilityModel}
        initialState={props.initialState}
        pageSizeOptions={[10, 20, 50, 100]}
        onRowClick={onRowClick ?? handleClick}
        getRowId={getRowId}
        loading={props.isLoading}
        slots={props.slots}
        slotProps={props.slotProps}
        disableColumnFilter={props.disableColumnFilter}
        disableColumnSelector={props.disableColumnSelector}
        disableDensitySelector={props.disableDensitySelector}
        pagination={props.pagination}
      />
    </React.Fragment>
  );
};

export default PortalDataGridPro;
